import { useCallback, useEffect, useState } from 'react'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  ConcreteTestSetTrackerConcreteTestSetsQuery,
  ConcreteTestSetTrackerConcreteTestSetsQueryResult,
  ConcreteTestSetTrackerRelationsQuery,
  ConcreteTestSetTrackerRelationsQueryResult,
  useConcreteTestSetTrackerConcreteTestSetsQuery,
  useConcreteTestSetTrackerRelationsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  UseCIMSModalValues,
  useCIMSModalValues,
} from 'components/CIMSModal/hooks/useCIMSModal'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { ApolloQueryResult, QueryResult } from '@apollo/client'
import { useAnyTrue } from 'hooks/useAnyTrue'
import { BreakDataModalState } from './components/BreakDataModal/types'

type TestSetQuery = ConcreteTestSetTrackerConcreteTestSetsQuery['project']
type RelationsQuery = ConcreteTestSetTrackerRelationsQuery['project']
type TestSet = TestSetQuery['concreteTestSets'][0]
type Relations = Pick<RelationsQuery, 'concretePlacements' | 'concreteTests'>
export type ConcreteTestSet = TestSet & Relations

type Refetch<T> = ApolloQueryResult<T>
type RefetchTestSets = Refetch<ConcreteTestSetTrackerConcreteTestSetsQuery>
type RefetchRelations = Refetch<ConcreteTestSetTrackerRelationsQuery>

type QueryResultOptions<T extends QueryResult> = Pick<T, 'refetch' | 'loading'>

interface ConcreteTestSetTrackerContext extends WithPayItemGlobalId {
  testSets: QueryResultOptions<ConcreteTestSetTrackerConcreteTestSetsQueryResult>
  relations: QueryResultOptions<ConcreteTestSetTrackerRelationsQueryResult>
  // rest
  concreteTestSets?: ConcreteTestSet[]
  fetching: boolean
  refetch: () => Promise<[RefetchTestSets, RefetchRelations]>
  documentChecklistItems?: TestSetQuery['documentChecklistItems']
  pdfModal: UseCIMSModalValues<ConcreteTestSet>
  uploadModal: UseCIMSModalValues<ConcreteTestSet>
  breakDataModal: UseCIMSModalValues<BreakDataModalState>
}

const { Provider, useContextFn } =
  createStrictContext<ConcreteTestSetTrackerContext>(
    'ConcreteTestSetTrackerContext'
  )

export const useConcreteTestSetTrackerContext = useContextFn

const ConcreteTestSetTrackerProvider: React.FC<WithPayItemGlobalId> = ({
  payItemGlobalId,
  children,
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  /**
   * because of how big concrete test sets are, and how many we have to get,
   * we're running simultaneous queries on purpose to get better perceived
   * performance in larger projects
   */
  const {
    data: testSets,
    loading: testSetsFetching,
    refetch: refetchTestSets,
  } = useConcreteTestSetTrackerConcreteTestSetsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId: projectId! },
    skip: !projectId,
  })
  const {
    data: relations,
    loading: relationsFetching,
    refetch: refetchRelations,
  } = useConcreteTestSetTrackerRelationsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId: projectId! },
    skip: !projectId,
  })
  const pdfModal = useCIMSModalValues<ConcreteTestSet>()
  const uploadModal = useCIMSModalValues<ConcreteTestSet>()
  const breakDataModal = useCIMSModalValues<BreakDataModalState>()
  const [concreteTestSets, setConcreteTestSets] = useState<ConcreteTestSet[]>(
    []
  )
  const refetch = useCallback<ConcreteTestSetTrackerContext['refetch']>(
    () => Promise.all([refetchTestSets(), refetchRelations()]),
    [refetchRelations, refetchTestSets]
  )
  useEffect(() => {
    const allRelations = [
      ...(relations?.project?.concretePlacements ?? []),
      ...(relations?.project?.concreteTests ?? []),
    ]
    const relationsMap = allRelations.reduce((map, relation) => {
      const key = relation.concreteTestSetGuid
      if (!key) return map
      const found = map.get(key) ?? {
        concretePlacements: [],
        concreteTests: [],
      }
      if (relation.__typename === 'ConcreteTest')
        found.concreteTests.push(relation)
      else if (relation.__typename === 'ConcretePlacement')
        found.concretePlacements.push(relation)
      return map.set(key, found)
    }, new Map<string, Relations>())
    setConcreteTestSets(
      (testSets?.project?.concreteTestSets ?? []).map<ConcreteTestSet>(
        (testSet) => {
          const { concretePlacements = [], concreteTests = [] } =
            relationsMap.get(testSet.globalId) ?? {}
          return {
            ...testSet,
            concretePlacements,
            concreteTests,
          }
        }
      )
    )
  }, [
    relations?.project?.concretePlacements,
    relations?.project?.concreteTests,
    testSets?.project?.concreteTestSets,
  ])
  const [fetching] = useAnyTrue([
    testSetsFetching ?? false,
    relationsFetching ?? false,
  ])
  return (
    <Provider
      value={{
        testSets: { loading: testSetsFetching, refetch: refetchTestSets },
        relations: { loading: relationsFetching, refetch: refetchRelations },
        payItemGlobalId,
        concreteTestSets,
        refetch,
        fetching,
        documentChecklistItems: testSets?.project?.documentChecklistItems,
        pdfModal,
        uploadModal,
        breakDataModal,
      }}
    >
      {children}
    </Provider>
  )
}

export default ConcreteTestSetTrackerProvider
